import { getStoredSession, saveSessionToStorage } from '../utils/sessionManager';

class AuthService {
  constructor() {
    this.baseHeaders = { 'Content-Type': 'application/json' };
    this.refreshThreshold = 5 * 60;
    this.refreshInterval = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.refreshEndpoint = process.env.REACT_APP_USER_API_URL 
      ? `${process.env.REACT_APP_USER_API_URL}/users/refresh-token`
      : null;
  }

  getAccessKey() {
    const session = getStoredSession();
    return session?.accessKey || null;
  }

  getRefreshToken() {
    const refreshTokenFromStorage = localStorage.getItem('refreshToken') || 
                                     localStorage.getItem('refresh_token') ||
                                     localStorage.getItem('aether_refresh_token');
    
    if (refreshTokenFromStorage) return refreshTokenFromStorage;
    
    const session = getStoredSession();
    if (!session) return null;
    
    return session.refreshToken || session.refresh_token || null;
  }

  getSession() {
    return getStoredSession();
  }

  isAuthenticated() {
    const session = getStoredSession();
    return !!(session?.accessKey && session?.orgId);
  }

  getAuthHeaders(additionalHeaders = {}) {
    const accessKey = this.getAccessKey();
    return {
      ...this.baseHeaders,
      ...additionalHeaders,
      ...(accessKey && { 'Authorization': `Bearer ${accessKey}` }),
    };
  }

  getTokenExpiry() {
    const session = getStoredSession();
    if (!session) return null;
    
    if (session.tokenExpiry) {
      return parseInt(session.tokenExpiry);
    }
    
    if (session.expiresIn) {
      const expiresInNum = parseInt(session.expiresIn);
      if (expiresInNum < 31536000) {
        return Math.floor(Date.now() / 1000) + expiresInNum;
      }
      return expiresInNum;
    }
    
    return null;
  }

  shouldRefreshToken() {
    const tokenExpiry = this.getTokenExpiry();
    if (!tokenExpiry) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    return (tokenExpiry - currentTime) <= this.refreshThreshold;
  }

  isTokenExpired() {
    const tokenExpiry = this.getTokenExpiry();
    if (!tokenExpiry) return true;
    
    return Math.floor(Date.now() / 1000) >= tokenExpiry;
  }

  async ensureTokenValid() {
    if (this.shouldRefreshToken()) {
      await this.refreshToken();
    }
  }

  async refreshToken() {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const session = getStoredSession();
        const refreshToken = this.getRefreshToken();
        
        if (!this.refreshEndpoint || !refreshToken) {
          return false;
        }

        const response = await fetch(this.refreshEndpoint, {
          method: 'POST',
          headers: this.baseHeaders,
          body: JSON.stringify({ refreshToken }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.accessToken) {
            const newRefreshToken = data.data.refreshToken || data.data.refresh_token || refreshToken;
            const updatedSession = {
              ...session,
              accessKey: data.data.accessToken,
              refreshToken: newRefreshToken,
              tokenExpiry: this.calculateTokenExpiry(data.data.expiresIn || 3600),
              expiresIn: data.data.expiresIn || 3600,
            };
            
            saveSessionToStorage(updatedSession);
            
            if (newRefreshToken) {
              localStorage.setItem('refreshToken', newRefreshToken);
              localStorage.setItem('aether_refresh_token', newRefreshToken);
            }
            
            return true;
          }
        }
        return false;
      } catch (error) {
        console.error('Token refresh error:', error);
        return false;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  calculateTokenExpiry(expiresIn) {
    const expiresInNum = parseInt(expiresIn);
    const currentTime = Math.floor(Date.now() / 1000);
    return expiresInNum > 31536000 ? expiresInNum : currentTime + expiresInNum;
  }

  updateSessionTokens(tokens) {
    const session = getStoredSession();
    if (!session) return;

    const updatedSession = {
      ...session,
      ...(tokens.accessKey && { accessKey: tokens.accessKey }),
      ...(tokens.accessToken && { accessKey: tokens.accessToken }),
      ...(tokens.refreshToken && { refreshToken: tokens.refreshToken }),
      ...(tokens.expiresIn && { 
        expiresIn: tokens.expiresIn,
        tokenExpiry: this.calculateTokenExpiry(tokens.expiresIn),
      }),
    };

    saveSessionToStorage(updatedSession);
    
    if (this.isAuthenticated()) {
      this.startTokenRefreshInterval();
    }
  }

  startTokenRefreshInterval() {
    this.stopTokenRefreshInterval();

    this.refreshInterval = setInterval(async () => {
      if (this.isAuthenticated() && this.shouldRefreshToken()) {
        await this.refreshToken();
      }
    }, 60000);
  }

  stopTokenRefreshInterval() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  clearSession() {
    this.stopTokenRefreshInterval();
    const keys = [
      'aether_session_data', 'aether_device_id', 'aether_org_id',
      'aether_access_key', 'aether_validation_shown', 'aether_validation_shown_session',
      'refreshToken', 'refresh_token', 'aether_refresh_token', 'aedrive_session_id',
    ];
    keys.forEach((k) => localStorage.removeItem(k));
  }

  async authenticatedFetch(url, options = {}) {
    await this.ensureTokenValid();
    
    const accessKey = this.getAccessKey();
    if (!accessKey) {
      throw new Error('No access key available');
    }

    const headers = {
      ...this.baseHeaders,
      ...options.headers,
      'Authorization': `Bearer ${accessKey}`,
    };

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401 || response.status === 403) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        const newAccessKey = this.getAccessKey();
        headers.Authorization = `Bearer ${newAccessKey}`;
        return await fetch(url, { ...options, headers });
      }
      throw new Error('Authentication failed - token refresh unsuccessful');
    }

    return response;
  }
}

const authService = new AuthService();
export default authService;
